import React, { useState } from "react";
import { FaUser, FaUsers, FaCheck, FaTimes } from 'react-icons/fa';
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";

import LoadingForButton from "./LoadingForButton";
import ConfirmModal from "./ConfirmModal";
import GuestService from "../services/guestService";
import { refreshDataGuest } from '../store/guestSlice';

const capitalizeWords = (str) =>
    str?.toLowerCase().split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');

const FamilyTablesView = ({
    tables,
    wedding,
    guest,
    attendingCount,
    setShowNotification,
    setNotificationMessage,
    setErrorResponse,
}) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedTableId, setSelectedTableId] = useState(null);
    const [message, setMessage] = useState('');
    const [isCancel, setIsCancel] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    // ── Helpers (iste kao u TablesReservation) ────────────────────────────
    const getLockDate = (wedding) => {
        const weddingDate = new Date(wedding.date);
        const lockDate = new Date(weddingDate);
        lockDate.setDate(lockDate.getDate() - (wedding.lockReservationDaysBefore || 3));
        return lockDate;
    };

    const getDaysUntilLock = (wedding) => {
        const lockDate = getLockDate(wedding);
        const now = new Date();
        const diffTime = lockDate - now;
        return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    };

    const canFamilyEditReservation = (wedding) => new Date() < getLockDate(wedding);
    const daysLeft = getDaysUntilLock(wedding);
    const canEdit = canFamilyEditReservation(wedding);

    const isChangingTable = !!guest.reservedTableId;
    const canReserveAtAll = attendingCount > 0;

    // ── Pluralization helper ──────────────────────────────────────────────
    const pluralizeOsoba = (n) => {
        if (n === 1) return 'osoba';
        if (n >= 2 && n <= 4) return 'osobe';
        return 'osoba';
    };

    const pluralizeMjesto = (n) => {
        if (n === 1) return 'mjesto';
        if (n >= 2 && n <= 4) return 'mjesta';
        return 'mjesta';
    };

    // ── Handlers ──────────────────────────────────────────────────────────
    const handleReserveTable = (tableId) => {
        if (!canEdit) {
            setNotificationMessage(t('reservationLockedMessage') || 'Rezervacije su zaključane.');
            setShowNotification(true);
            setErrorResponse(true);
            setTimeout(() => { setShowNotification(false); setErrorResponse(false); }, 3000);
            return;
        }

        const table = tables.find(t => t._id === tableId);
        setSelectedTableId(tableId);

        if (isChangingTable) {
            const lines = [`Promijeniti stol na "${capitalizeWords(table.name)}"?`];
            lines.push(`Rezervirat će se ${attendingCount} ${pluralizeMjesto(attendingCount)} za članove obitelji koji dolaze.`);
            lines.push(`Napomena: promjena stola povećava broj otkazivanja.`);
            setMessage(lines.join('\n'));
        } else {
            const lines = [`Rezervirati stol "${capitalizeWords(table.name)}"?`];
            lines.push(`Rezervirat će se ${attendingCount} ${pluralizeMjesto(attendingCount)} za članove obitelji koji dolaze.`);
            setMessage(lines.join('\n'));
        }

        setIsCancel(false);
        setIsModalOpen(true);
    };

    const handleCancelReservation = () => {
        if (!canEdit) {
            setNotificationMessage(t('reservationLockedMessage') || 'Rezervacije su zaključane.');
            setShowNotification(true);
            setErrorResponse(true);
            setTimeout(() => { setShowNotification(false); setErrorResponse(false); }, 3000);
            return;
        }
        setMessage(t('confirmCancelReservation') || 'Želite li otkazati rezervaciju stola?');
        setIsCancel(true);
        setIsModalOpen(true);
    };

    const handleConfirmAction = () => {
        setIsModalOpen(false);
        setIsLoading(true);

        if (isCancel) {
            GuestService.cancelFamilyReservation()
                .then(() => {
                    dispatch(refreshDataGuest());
                    setNotificationMessage(t('reservationCancelled') || 'Rezervacija je otkazana.');
                    setShowNotification(true);
                    setTimeout(() => setShowNotification(false), 3000);
                    setSelectedTableId(null);
                    setIsLoading(false);
                    setIsCancel(false);
                })
                .catch((err) => {
                    const msg = err.response?.data?.message || t('genericError') || 'Greška.';
                    setNotificationMessage(msg);
                    setShowNotification(true);
                    setErrorResponse(true);
                    setTimeout(() => { setShowNotification(false); setErrorResponse(false); }, 4000);
                    setSelectedTableId(null);
                    setIsLoading(false);
                    setIsCancel(false);
                });
            return;
        }

        GuestService.reserveFamilyTable({ tableId: selectedTableId })
            .then(() => {
                dispatch(refreshDataGuest());
                setNotificationMessage(t('tableReservedSuccess') || 'Stol je rezerviran.');
                setShowNotification(true);
                setTimeout(() => setShowNotification(false), 3000);
                setSelectedTableId(null);
                setIsLoading(false);
            })
            .catch((err) => {
                const msg = err.response?.data?.message || t('genericError') || 'Greška.';
                setNotificationMessage(msg);
                setShowNotification(true);
                setErrorResponse(true);
                setTimeout(() => { setShowNotification(false); setErrorResponse(false); }, 4000);
                setSelectedTableId(null);
                setIsLoading(false);
            });
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setSelectedTableId(null);
        setMessage('');
        setIsCancel(false);
    };

    return (
        <div className="flex w-full p-4">
            <div className="rounded-md w-full">

                {/* ── Header info ────────────────────────────────────────── */}
                <div className="flex flex-col gap-3 mb-4">
                    {guest?.reservedTableId ? (
                        <h2 className="text-xl">
                            {t("currentTable", { name: tables?.find(table => table._id === guest?.reservedTableId)?.name })}
                        </h2>
                    ) : (
                        <h2 className="text-xl">{t("noTableReserved") || 'Nema rezerviranog stola'}</h2>
                    )}

                    {/* Info: koliko mjesta se rezervira */}
                    {canReserveAtAll ? (
                        <div className="bg-rose-50 border border-rose-200 rounded-xl p-3 text-sm text-rose-700">
                            <p className="font-semibold mb-1 flex items-center gap-2">
                                <FaUsers /> Rezervacija za obitelj ({attendingCount} {pluralizeOsoba(attendingCount)})
                            </p>
                            <p className="text-rose-600 text-xs">
                                Rezervirat će se {attendingCount} {pluralizeMjesto(attendingCount)} za članove koji su potvrdili dolazak.
                            </p>
                        </div>
                    ) : (
                        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-sm text-amber-700">
                            <p className="flex items-center gap-2">
                                ⚠️ Nitko iz obitelji još nije potvrdio dolazak. Možete pregledati stolove, ali rezervacija nije moguća dok barem jedan član ne potvrdi.
                            </p>
                        </div>
                    )}

                    {/* Info o promjeni stola */}
                    {isChangingTable && canReserveAtAll && (
                        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-sm text-amber-700">
                            <p className="flex items-center gap-2">
                                ⚠️ Promjena stola povećava broj otkazivanja.
                            </p>
                        </div>
                    )}

                    {/* Deadline za rezervaciju */}
                    {!guest?.reservedTableId && (
                        canEdit ? (
                            <div className={`p-4 rounded-lg border-l-4 ${daysLeft <= 3 ? 'bg-orange-50 border-orange-500' : 'bg-blue-50 border-blue-500'}`}>
                                <div className="flex items-start gap-3">
                                    <span className="text-2xl">⏰</span>
                                    <div>
                                        <h4 className="font-semibold text-gray-800 mb-1">{t('reservationDeadline') || 'Rok za rezervaciju'}</h4>
                                        <p className={daysLeft <= 3 ? 'text-orange-700' : 'text-blue-700'}>
                                            {daysLeft > 0
                                                ? (t('daysUntilLock', { days: daysLeft, date: getLockDate(wedding).toLocaleDateString() })
                                                    || `Još ${daysLeft} dana do zaključavanja (${getLockDate(wedding).toLocaleDateString()})`)
                                                : (t('lastDayToReserve') || 'Posljednji dan za rezervaciju!')}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-lg">
                                <div className="flex items-start gap-3">
                                    <span className="text-2xl">🔒</span>
                                    <div>
                                        <h4 className="font-semibold text-red-800 mb-1">{t('reservationsLocked') || 'Rezervacije zaključane'}</h4>
                                        <p className="text-red-700">{t('reservationLockedMessage') || 'Više nije moguće mijenjati rezervaciju.'}</p>
                                    </div>
                                </div>
                            </div>
                        )
                    )}

                    {/* Cancel count upozorenje */}
                    {guest?.reservationCancelCount >= 2 && (
                        <div className={`flex flex-wrap justify-between sm:flex-row sm:gap-7 text-sm md:text-xl font-medium px-4 py-2 rounded-xl
                            ${guest?.reservationCancelCount >= 5 ? 'bg-red-200 text-red-800'
                                : guest?.reservationCancelCount >= 3 ? 'bg-yellow-200 text-yellow-800'
                                    : 'bg-green-100 text-green-800'}`}>
                            {t("reservationCancelInfo", {
                                count: guest?.reservationCancelCount || 0,
                                remaining: 5 - (guest?.reservationCancelCount || 0)
                            }) || `Broj otkazivanja: ${guest?.reservationCancelCount || 0}/5`}
                            {guest?.reservationCancelCount >= 5 && <p>{t("reservationLimitReached") || 'Limit otkazivanja dosegnut.'}</p>}
                        </div>
                    )}
                </div>

                {tables?.length === 0 && (
                    <div className="w-full text-center text-gray-500 text-lg py-10">{t("noTables") || 'Nema dostupnih stolova.'}</div>
                )}

                {/* ── Grid stolova ────────────────────────────────────────── */}
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                    {tables?.filter((table) => {
                        const isCurrentTable = String(guest?.reservedTableId) === String(table._id);
                        if (isCurrentTable) return true;
                        if (table.isLocked) return false;
                        // Ako je obitelj tek u pregledu (nije potvrdila), prikaži sve stolove koji imaju barem 1 slobodno
                        const tableFreeSeats = table.seats - table.reservedSeats;
                        const seatsNeeded = canReserveAtAll ? attendingCount : 1;
                        if (tableFreeSeats < seatsNeeded) return false;
                        return true;
                    }).map((table) => {
                        const tableFreeSeats = table.seats - table.reservedSeats;
                        const isCurrentTable = String(guest?.reservedTableId) === String(table._id);

                        const canReserve = canReserveAtAll &&
                            !isCurrentTable &&
                            !table.isLocked &&
                            !guest?.onLockedTable &&
                            tableFreeSeats >= attendingCount &&
                            canEdit;

                        return (
                            <div
                                key={table._id}
                                className={`flex flex-col ${isCurrentTable
                                    ? 'bg-blue-300 border-blue-500'
                                    : 'bg-white border border-gray-500'
                                    } rounded-xl p-4 shadow-md hover:shadow-lg transition-all border`}
                            >
                                <div className="flex justify-between items-start mb-2">
                                    <h2 className="text-2xl italic font-semibold capitalize">
                                        {t('tableLabel', { name: table.name }) || table.name}
                                    </h2>
                                </div>

                                <div className="flex justify-between items-center gap-4 text-gray-800 text-md mb-4 mt-2">
                                    <div className="flex justify-center items-center">
                                        <FaUsers className="mr-2" />
                                        <span>{table.reservedSeats}</span>
                                        <span>/</span>
                                        <span>{t('seatsLabel', { seats: table.seats }) || `${table.seats} mjesta`}</span>
                                    </div>
                                    <div className="bg-green-200 text-green-900 text-sm px-3 py-1 rounded-full">
                                        {t('freeSeatsLabel', { free: tableFreeSeats }) || `${tableFreeSeats} slobodno`}
                                    </div>
                                </div>

                                {/* Lista gostiju za stolom — obitelji i obični gosti */}
                                <div className="flex-1 mb-6">
                                    <h1 className="mb-2">{t("guestsAtTable") || 'Gosti za stolom:'}</h1>
                                    {table.reservedSeats > 0 ? (
                                        <ul className="space-y-2">
                                            {table.guests.map((g, index) => {
                                                // Obitelj
                                                if (g.isFamilyGroup) {
                                                    const attendingMembers = (g.familyMembers || [])
                                                        .filter(m => m.attending === true);
                                                    return (
                                                        <li key={index} className="flex flex-col gap-1 bg-rose-50 border border-rose-100 rounded-lg px-2 py-1.5">
                                                            <div className="flex items-center gap-2 text-gray-900">
                                                                <FaUsers className="text-rose-500 text-xs" />
                                                                <span className="capitalize font-semibold">{capitalizeWords(g.fullName)}</span>
                                                                <span className="ml-auto text-xs text-rose-500">
                                                                    {attendingMembers.length} {pluralizeOsoba(attendingMembers.length)}
                                                                </span>
                                                            </div>
                                                            {attendingMembers.map((m, mIdx) => (
                                                                <div key={mIdx} className="flex items-center gap-2 text-gray-600 text-sm pl-5">
                                                                    <FaUser className="text-gray-300 text-xs" />
                                                                    <span className="capitalize">{capitalizeWords(m.name)}</span>
                                                                </div>
                                                            ))}
                                                        </li>
                                                    );
                                                }

                                                // Običan gost
                                                return (
                                                    <li key={index} className="flex flex-col gap-1">
                                                        <div className="flex items-center gap-2 text-gray-900">
                                                            <FaUser /> {capitalizeWords(g.fullName)}
                                                        </div>
                                                        {g.companions?.unregistered > 0 && (
                                                            <div className="flex items-center gap-2 text-gray-500 text-sm pl-6">
                                                                <FaUser className="text-xs" />
                                                                + {g.companions.unregistered} dodatna mjesta
                                                            </div>
                                                        )}
                                                    </li>
                                                );
                                            })}
                                        </ul>
                                    ) : (
                                        <p className="text-center text-gray-500">{t("noGuestsAtTable") || 'Nema gostiju za stolom.'}</p>
                                    )}
                                </div>

                                <div className={`flex flex-col gap-2 ${guest?.reservationCancelCount >= 5 ? 'hidden' : ''}`}>
                                    {canReserve && (
                                        <button
                                            onClick={() => handleReserveTable(table._id)}
                                            disabled={!canEdit}
                                            className={`submitButton w-full flex items-center justify-center gap-2 ${!canEdit ? 'opacity-50 cursor-not-allowed' : ''}`}
                                        >
                                            {isLoading && selectedTableId === table._id ? (
                                                <LoadingForButton />
                                            ) : isChangingTable ? (
                                                'Premjesti obitelj za ovaj stol'
                                            ) : (
                                                <><FaUsers /> Rezerviraj za obitelj ({attendingCount})</>
                                            )}
                                        </button>
                                    )}

                                    {/* Samo pregled — obitelj nije potvrdila */}
                                    {!canReserveAtAll && !isCurrentTable && !table.isLocked && tableFreeSeats > 0 && canEdit && (
                                        <div className="text-center text-xs text-gray-400 italic py-2">
                                            Potvrdite tko iz obitelji dolazi da biste mogli rezervirati.
                                        </div>
                                    )}

                                    {isCurrentTable && (
                                        <div className="flex flex-col items-center bg-white text-gray-800 p-3 rounded-xl gap-2">
                                            <span className="flex items-center gap-1">
                                                <FaCheck className="text-green-500" /> {t("yourCurrentTable") || 'Vaš trenutni stol'}
                                            </span>
                                            <button
                                                onClick={handleCancelReservation}
                                                className={`${guest?.reservationCancelCount >= 4 || guest?.onLockedTable ? 'hidden' : ''} text-red-600 text-sm underline hover:text-red-800 transition`}
                                            >
                                                {t("cancelReservation") || 'Otkaži rezervaciju'}
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            <ConfirmModal
                isOpen={isModalOpen}
                onRequestClose={handleCloseModal}
                onConfirm={handleConfirmAction}
                message={message}
            />
        </div>
    );
};

export default FamilyTablesView;
